import React, { forwardRef, useContext, useEffect } from 'react';
import styled from 'styled-components';
import 'polished';
import '@s-group/design-system-tokens/web/tokens/size.es6.js';
import { usePrevious } from '../common/hooks.js';
import { TabsContext } from './TabsContext.js';
import { TabListItem } from './TabListItem.js';
import { TabListStyle } from './Tabs.styles.js';
import { useRefMap } from './hooks.js';
import { mod } from './util.js';

const TabListWrapper = forwardRef(({ alignment, className, color, iconPos, onTabSelect, sizing, variant, ...rest }, ref) => {
    const tabContext = useContext(TabsContext);
    const [getRefMap, refCallback] = useRefMap();
    const previousSelected = usePrevious(tabContext === null || tabContext === void 0 ? void 0 : tabContext.selected);
    const getTabIndex = (items, ref) => items.findIndex(({ ref: itemRef }) => ref === itemRef);
    useEffect(() => {
        if (typeof onTabSelect === 'function' && tabContext) {
            const { selected } = tabContext;
            if (!!selected && selected !== previousSelected)
                onTabSelect(selected, getTabIndex(tabContext.items, selected));
        }
    }, [onTabSelect, tabContext, previousSelected]);
    if (!(tabContext === null || tabContext === void 0 ? void 0 : tabContext.items.length)) {
        return null;
    }
    const handleClick = (ref) => () => {
        tabContext.dispatch({
            type: 'select',
            payload: { ref },
        });
    };
    const handleKeyUp = (tabPanelRef) => (event) => {
        const tabCount = tabContext.items.length;
        const currentIndex = getTabIndex(tabContext.items, tabPanelRef);
        if (tabCount === 0 || currentIndex < 0) {
            return; // No selected tab
        }
        const newIndex = {
            ArrowLeft: mod(currentIndex - 1, tabCount),
            ArrowRight: mod(currentIndex + 1, tabCount),
            Home: 0,
            End: tabCount - 1,
        }[event.key];
        if (typeof newIndex !== 'undefined' &&
            newIndex !== currentIndex) {
            tabContext.dispatch({
                type: 'select',
                payload: {
                    ref: tabContext.items[newIndex].ref,
                },
            });
            const newSelectedTab = getRefMap().get(newIndex);
            newSelectedTab === null || newSelectedTab === void 0 ? void 0 : newSelectedTab.focus();
            newSelectedTab === null || newSelectedTab === void 0 ? void 0 : newSelectedTab.scrollIntoView();
        }
    };
    return (React.createElement("div", { className: className, tabIndex: -1, ref: ref },
        React.createElement("div", { ...rest, role: "tablist" }, tabContext === null || tabContext === void 0 ? void 0 : tabContext.items.map(({ props: { icon, id, label, labelProps }, ref }, index) => {
            return (React.createElement(TabListItem, { alignment: alignment, "aria-controls": id, color: color, icon: icon, iconPos: iconPos, id: `${id}tablabel`, key: id, onClick: handleClick(ref), onKeyUp: handleKeyUp(ref), selected: tabContext.selected === ref, sizing: sizing, variant: variant, ...labelProps, ref: refCallback(index) }, label));
        }))));
});
TabListWrapper.displayName = 'TabListWrapper';
/**
 * `<TabList>` component is used internally to render tab controls.
 *
 * @internal
 */
const TabList = styled(TabListWrapper).attrs(() => {
    const tabContext = useContext(TabsContext);
    return {
        $itemCount: tabContext === null || tabContext === void 0 ? void 0 : tabContext.items.length,
    };
}) `
    ${TabListStyle}
`;

export { TabList };
//# sourceMappingURL=TabList.js.map
